import { useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { mdiPencilOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import Select from "components/Select";
import { FR_CONTRACT_TYPE, FR_STATUS } from "constants/haulpay-broker";
import FRLineItems from "features/main/HaulPay/modules/Broker/FRLineItems";
import { formatNumber } from "helpers/formatNumber";
import { useUserFeature } from "hooks/useUserFeature";
import { ICompany, IFactoringCompany, IFundingRequest } from "models/HaulPay";
import { selectAccount } from "store/auth/selector";
import styles from "./style.module.scss";
import { selectFactoringProfile } from "store/user/selector";
import DollarInput from "components/DollarInput";
import DatePicker from "../../../../../../components/DatePicker";
import DebtorSearch from "../../../../../../components/DebtorSearchInput";

interface ISharedValue {
  isGlobalDisabled: boolean;
  setFundingRequestValue: React.Dispatch<React.SetStateAction<string>>;
  factoringCompany?: IFactoringCompany;
  carrierCanEdit: boolean;
  companyValue?: ICompany;
  isFactoringCompany?: boolean;
  isAutoGenerateLoadNumbersEnabled?: boolean;
}

export interface IEditFundingRequest extends IFundingRequest {
  carrierEmail?: string;
  carrierPhone?: string;
  carrierPaymentMethod?: string;
  carrierMc?: string;
  debtorEmail?: string;
}

export default function StepOne({
  isGlobalDisabled,
  setFundingRequestValue,
  carrierCanEdit,
  companyValue,
  isFactoringCompany,
  isAutoGenerateLoadNumbersEnabled
}: Readonly<ISharedValue>) {
  const { isCarrier, isAllowLineItems } = useUserFeature();
  const [shipperInvoiceAmount, setShipperInvoiceAmount] = useState<number>();
  const { userPermission } = useSelector(selectAccount);
  const factoring = useSelector(selectFactoringProfile);
  const useNewNFRequest = factoring?.use_new_non_factored_funding_requests;
  const [isEditInvoiceAmount, toggleEditInvoiceAmountModal] = useToggle();
  const frTypeOptions = FR_CONTRACT_TYPE.filter(
    (request) => userPermission.available_contract_types[request.value]
  );

  // submit
  const {
    register,
    setValue,
    control,
    clearErrors,
    formState: { errors }
  } = useFormContext<Partial<IEditFundingRequest>>();

  const invoiceAmount = useWatch({ control: control, name: "shipperInvoiceAmount" });
  const fundingRequestTypeValue = useWatch({
    control: control,
    name: "fundingRequestType.value"
  });

  const handleValueChange = (value: string) => {
    setFundingRequestValue(value);
    if (
      (value === "NON_FACTORED_STANDARD" || value === "NON_FACTORED_BILL_OUT") &&
      !useNewNFRequest
    ) {
      setValue(
        "fundingRequestStatus",
        value === "NON_FACTORED_STANDARD" ? FR_STATUS[5] : FR_STATUS[6]
      );
    }
  };
  const onChangeLineItemAmount = (amount: number) => {
    setShipperInvoiceAmount(amount ?? 0);
  };
  const handleAmountUpdate = () => {
    setValue("shipperInvoiceAmount", shipperInvoiceAmount ?? ("" as any));
    toggleEditInvoiceAmountModal();
  };

  useEffect(() => {
    setShipperInvoiceAmount(invoiceAmount);
  }, [invoiceAmount]);

  return (
    <div className={styles.fundingRequestStepOne}>
      <div className={styles.container}>
        <div className={styles.section}>
          <Input label="&nbsp;&nbsp;Invoice #" name="invoiceNumber" register={register} disabled />
          <Input
            label="&nbsp;&nbsp;Load #"
            name="userLoadNumber"
            register={register}
            disabled={isAutoGenerateLoadNumbersEnabled && (!isCarrier || isGlobalDisabled)}
          />
        </div>

        {!isCarrier ? (
          <>
            <Controller
              name="fundingRequestType"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={value}
                  onChange={(value) => {
                    onChange(value);
                    handleValueChange(value.value);
                  }}
                  disabled={!carrierCanEdit || isGlobalDisabled}
                  options={frTypeOptions}
                  label="&nbsp;Funding Request Type"
                />
              )}
            />
            <Controller
              name="createdAt"
              control={control}
              render={({ field: { value } }) => (
                <DatePicker
                  label="&nbsp;&nbsp;Created At"
                  placeholder="Select Date & Time"
                  value={value}
                  disabled
                />
              )}
            />
            <DebtorSearch
              name="debtor"
              value={companyValue?.dba || ""}
              onSelect={(company: ICompany) => {
                setValue("debtor.phoneNumber", company.phone_number);
                setValue("debtor.email", company.email);
                setValue("debtor", company);
                clearErrors("debtor");
              }}
              label={
                <div className="pl-2">
                  <span>Shipper/Debtor </span>
                  <span className="italic">(Carriers will not see this)</span>
                </div>
              }
              error={errors?.debtor as any}
              disabled={fundingRequestTypeValue === FR_CONTRACT_TYPE[3].value}
              isConnectedDebtor
            />
            <div className={styles.section}>
              <DollarInput
                label="&nbsp;&nbsp;Carrier Fee"
                name="carrierFee"
                disabled={!isCarrier || isGlobalDisabled}
              />
              <DollarInput
                label="&nbsp;&nbsp;Carrier Pay"
                name="carrierPay"
                disabled={!isCarrier || isGlobalDisabled}
              />
            </div>

            <div className={styles.section}>
              <Input
                label={isCarrier ? "Rate" : "Carrier Rate"}
                name="carrierRate"
                labelClass="pl-1"
                register={register}
                disabled
              />
              <DollarInput label="&nbsp;&nbsp;Carrier Amount Funded" name="amountFunded" disabled />
            </div>
            <DollarInput
              name="shipperInvoiceAmount"
              label={
                <div className={styles.selectBoxLabel}>
                  <span className="pl-1">Shipper Invoice Amount</span>
                  {isAllowLineItems ? (
                    <button
                      type="button"
                      onClick={toggleEditInvoiceAmountModal}
                      className={styles.selectBoxLabelRight}>
                      <Icon path={mdiPencilOutline} className="flex-none size-3.5" />
                      <span>(Edit line item)</span>
                    </button>
                  ) : null}
                </div>
              }
              disabled={isAllowLineItems || isGlobalDisabled}
            />
            <div className={styles.sectionTwo}>
              <Input
                label="Shipper/Debtor Phone"
                name="debtor.phoneNumber"
                register={register}
                disabled
              />
            </div>
            <Input label="Carrier Phone" name="carrierPhone" register={register} disabled />
            <Input
              label={
                <p>
                  <span className="pl-1">Carrier Payment Method</span>
                  <>
                    {isFactoringCompany ? (
                      <span className="pl-1 font-semibold text-error">Factor Will Be Paid</span>
                    ) : null}
                  </>
                </p>
              }
              name="carrierPaymentMethod"
              register={register}
              disabled
            />
          </>
        ) : null}

        {isCarrier && (
          <DollarInput
            label="&nbsp;Invoice Amount"
            name="shipperInvoiceAmount"
            disabled={!carrierCanEdit || !isCarrier || isGlobalDisabled}
            error={errors.shipperInvoiceAmount}
          />
        )}
        <div className={styles.section}>
          {!isCarrier ? (
            <>
              <DollarInput
                label="&nbsp;&nbsp;Broker Gross Margin"
                name="brokerGrossMargin"
                disabled={!isCarrier || isGlobalDisabled}
              />
              <DollarInput
                label="&nbsp;&nbsp;Reserve Holdback"
                name="reserveHoldBack"
                disabled={!isCarrier || isGlobalDisabled}
              />
            </>
          ) : (
            <Input
              label={isCarrier ? "Rate" : "Carrier Rate"}
              name="carrierRate"
              labelClass="pl-1"
              register={register}
              disabled
            />
          )}
        </div>

        {!isCarrier && (
          <Input label="Shipper/Debtor Email" name="debtor.email" register={register} disabled />
        )}

        {isCarrier ? (
          <Input
            label="&nbsp;&nbsp;Payment Method"
            name="paymentMethod"
            value="ACH"
            register={register}
            disabled
          />
        ) : null}
      </div>
      <Modal
        isOpen={isEditInvoiceAmount}
        closeModal={toggleEditInvoiceAmountModal}
        wrapperClass="text-sm desktop:!min-h-fit desktop:!w-1/3"
        dialogClass="!bg-foreground !p-4 !border !border-card !overflow-y-auto gap-4"
        closeClass="!top-24 !right-6 !bg-transparent !text-placeholder">
        <h3 className="text-xl font-bold text-primary">Funding Request Line Items</h3>
        <div className="flex flex-col gap-3">
          <FRLineItems onChangeLineItemAmount={onChangeLineItemAmount} />
          <div className="flex justify-between">
            <span>Shipper Invoice Amount</span>
            <span>{formatNumber(shipperInvoiceAmount ?? 0, 2)}</span>
          </div>
        </div>
        <Button type="button" onClick={handleAmountUpdate} className="self-end w-20">
          Done
        </Button>
      </Modal>
    </div>
  );
}
